import { LOGGED_ACTIVITY_TYPES, activityTypeLabel, withOptions } from "../help/fieldHints";

/** Sales-plan KPI a logged activity can count toward. Blank means it is stored only. */
export const ACTIVITY_MEASUREMENTS: Array<{ value: string; label: string }> = [
  { value: "activity_call", label: "Cold calls" },
  { value: "activity_email", label: "Emails" },
  { value: "activity_meeting", label: "Meetings" },
  { value: "activity_demo", label: "Demos" },
  { value: "", label: "Does not count" },
];

const MEASUREMENT_BY_VALUE: Record<string, string | null> = {
  cold_call: "activity_call",
  email: "activity_email",
  meeting: "activity_meeting",
  demo: "activity_demo",
  note: null,
};

export type ActivityType = {
  value: string;
  label: string;
  measurement: string | null;
  meaning?: string;
};

export const DEFAULT_ACTIVITY_TYPES: ActivityType[] = LOGGED_ACTIVITY_TYPES.map((item) => ({
  value: item.value,
  label: item.label,
  measurement: MEASUREMENT_BY_VALUE[item.value] ?? null,
  meaning: item.meaning,
}));

const MEASUREMENT_MEANING: Record<string, string> = {
  activity_call: "Counted on the owner's sales plan as Cold calls.",
  activity_email: "Counted on the owner's sales plan as Emails.",
  activity_meeting: "Counted on the owner's sales plan as Meetings.",
  activity_demo: "Counted on the owner's sales plan as Demos.",
};

export function meaningForActivityType(type: Pick<ActivityType, "measurement" | "meaning">): string {
  if (type.meaning) return type.meaning;
  if (!type.measurement) return "Not counted on the sales plan.";
  return MEASUREMENT_MEANING[type.measurement] ?? "Counted on the owner's sales plan.";
}

export function activityTypesHint(types: ActivityType[]): string {
  return withOptions(
    "What you did with the company or contact. The organisation chooses this list under Settings, Company Info. A type that counts toward a sales-plan KPI updates the owner's plan.",
    types.map((item) => ({ label: item.label, meaning: meaningForActivityType(item) })),
  );
}

export function labelForActivityType(value: string | null | undefined, types: ActivityType[]): string {
  const raw = (value || "").trim().toLowerCase();
  const found = types.find((item) => item.value === raw);
  if (found) return found.label;
  return activityTypeLabel(value);
}

/** Stable key stored on the activity. Renaming the label does not change it. */
export function activityTypeValue(label: string, taken: Iterable<string>): string | null {
  const base = label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 32);
  // A blank name has nothing to key. Prefixing it produced the key "type".
  if (!base) return null;
  let stem = /^[a-z]/.test(base) ? base : `type_${base}`;
  stem = stem.slice(0, 32);
  if (!/^[a-z][a-z0-9_]{0,31}$/.test(stem)) return null;
  const used = new Set(taken);
  if (!used.has(stem)) return stem;
  for (let n = 2; n < 100; n += 1) {
    const suffix = `_${n}`;
    const next = `${stem.slice(0, 32 - suffix.length)}${suffix}`;
    if (!used.has(next)) return next;
  }
  return null;
}

export function sameActivityTypes(left: ActivityType[], right: ActivityType[]): boolean {
  if (left.length !== right.length) return false;
  return left.every((item, index) => {
    const other = right[index];
    return item.value === other.value && item.label === other.label && item.measurement === other.measurement;
  });
}
